/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import HeaderClientes from "components/_refactor/headers/clients";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import TransferList from "components/List/TransferList";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);

function mergeCondicoesPorId(listA, listB) {
  const map = new Map();
  [...listA, ...listB].forEach((item) => {
    if (item?.IdCondicaoPagamento != null) {
      map.set(item.IdCondicaoPagamento, item);
    }
  });
  return Array.from(map.values());
}

function normalizeCondicao(row) {
  const id = row.IdCondicaoPagamento ?? row.Id;
  const politica = row.Politica ?? row.Descricao ?? row.Nome ?? "";
  const prazo = row.PrazoMedio ?? row.PrazoMedioDias ?? "";
  const encargo = row.PercentualEncargo ?? row.percentualEncargo ?? null;

  return {
    IdCondicaoPagamento: id,
    Politica: politica,
    PrazoMedio: prazo,
    TextoLista: politica,
    TooltipDetalhes:
      encargo != null && encargo !== "" ? `Encargo: ${encargo}%` : undefined,
  };
}

function vitrineIdsIguais(a, b) {
  const ids = (arr) =>
    [...(arr || [])]
      .map((x) => x?.IdCondicaoPagamento)
      .filter((x) => x != null)
      .sort()
      .join(",");
  return ids(a) === ids(b);
}

export default function GestaoCondicoesComerciais() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const isMobile = IsMobile();

  const marcas = useMemo(
    () =>
      JSON.parse(localStorage.getItem("usuarioMarcas")).filter(
        (x) => x?.IdMarca != 0,
      ) || [],
    [],
  );

  const [marcaSelecionada, setMarcaSelecionada] = useState(marcas[0]);
  const [fullCatalog, setFullCatalog] = useState([]);
  const [vinculadas, setVinculadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const snapshotRef = useRef({ vinculadas: [] });

  const fetchCondicoes = useCallback(async () => {
    if (!marcaSelecionada?.IdMarca) {
      setFullCatalog([]);
      setVinculadas([]);
      return;
    }

    setLoading(true);
    try {
      const res = await Api.get(
        "b2b-showcase-payment-conditions",
        "showcaseB2B",
        `?idMarca=${marcaSelecionada.IdMarca}`,
        true,
      );

      if (res?.Success && res.Result) {
        const d = res.Result;
        const disp = (d.CondicoesDisponiveis || d.Disponiveis || []).map(
          normalizeCondicao,
        );
        const vinc = (d.CondicoesVitrine || d.Vinculadas || []).map(
          normalizeCondicao,
        );
        const pool = mergeCondicoesPorId(disp, vinc);

        setFullCatalog(pool);
        setVinculadas(vinc);
        snapshotRef.current = { vinculadas: vinc };
      } else {
        setFullCatalog([]);
        setVinculadas([]);
        snapshotRef.current = { vinculadas: [] };
      }
    } catch (error) {
      snackbar.showMessage(
        typeof error === "string"
          ? error
          : "Não foi possível carregar as condições de pagamento. Verifique se o endpoint está disponível.",
        { severity: "error" },
      );
      setFullCatalog([]);
      setVinculadas([]);
      snapshotRef.current = { vinculadas: [] };
    } finally {
      setLoading(false);
    }
  }, [marcaSelecionada?.IdMarca]);

  useEffect(() => {
    fetchCondicoes();
  }, [fetchCondicoes]);

  const initialLeft = useMemo(
    () =>
      fullCatalog.filter(
        (c) =>
          !vinculadas.some(
            (v) => v.IdCondicaoPagamento === c.IdCondicaoPagamento,
          ),
      ),
    [fullCatalog, vinculadas],
  );

  const isDirty = useMemo(
    () => !vitrineIdsIguais(vinculadas, snapshotRef.current?.vinculadas),
    [vinculadas],
  );

  const handleRightChange = useCallback((value) => {
    if (!Array.isArray(value)) return;
    setVinculadas((prev) => (vitrineIdsIguais(prev, value) ? prev : value));
  }, []);

  const handleConfirmar = async () => {
    if (!marcaSelecionada?.IdMarca || saving) return;

    setSaving(true);
    try {
      const res = await Api.put(
        "put-b2b-payment-conditions-selection",
        "showcaseB2B",
        {
          IdMarca: marcaSelecionada.IdMarca,
          IdsCondicoesVitrine: vinculadas
            .map((v) => v.IdCondicaoPagamento)
            .filter((id) => id != null),
        },
        true,
      );

      if (res?.Success) {
        snackbar.showMessage(
          "Condições da vitrine B2B atualizadas com sucesso.",
          {
            severity: "success",
          },
        );
        snapshotRef.current = { vinculadas: [...vinculadas] };
        await fetchCondicoes();
      } else {
        snackbar.showMessage(
          res?.Message || "Não foi possível salvar as alterações.",
          { severity: "error" },
        );
      }
    } catch (error) {
      snackbar.showMessage(
        typeof error === "string"
          ? error
          : "Erro ao salvar condições de pagamento da vitrine B2B.",
        { severity: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = () => {
    const snap = snapshotRef.current?.vinculadas || [];
    setVinculadas([...snap]);
  };

  return (
    <>
      <HeaderClientes
        section="Vitrine B2B"
        subsection="Gestão de condições de pagamento"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <CardHeader
                className={classes.cardHeader}
                title="Vitrine B2B – Gestão de condições de pagamento"
                titleTypographyProps={{ variant: "h3", component: Box }}
              />
              <Box px={4} pb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <AutoCompleteField
                      label="Marca"
                      options={marcas}
                      optionIdKey="IdMarca"
                      optionLabelKey="Nome"
                      value={marcaSelecionada}
                      onChange={setMarcaSelecionada}
                      disabled={loading || saving || marcas.length === 0}
                      loading={false}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TransferList
                      loading={loading}
                      initialLeft={initialLeft}
                      initialRight={vinculadas}
                      leftLabel="Condição de pagamento"
                      rightLabel="Condição de pagamento a serem utilizadas em Vitrine B2B"
                      onRightChange={handleRightChange}
                      itemLabelKey="TextoLista"
                      itemExtraLabelKey="TooltipDetalhes"
                      itemIdKey="IdCondicaoPagamento"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      flexDirection={isMobile ? "column" : "row"}
                      style={{ gap: 12 }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={
                          !marcaSelecionada || loading || saving || !isDirty
                        }
                        onClick={handleConfirmar}
                      >
                        {saving ? "Salvando..." : "Confirmar"}
                      </Button>
                      <Button
                        variant="contained"
                        style={{ backgroundColor: "#e53935", color: "#fff" }}
                        disabled={loading || saving || !isDirty}
                        onClick={handleCancelar}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
